// Common Types
export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string | null;
  cycles?: number | null;
  citations?: string[];
}

export interface Document {
  content: string;
  sources: string;
  urls: string;
}

export interface ExecutionResult {
  tool: string;
  status: string;
  args: Record<string, any>;
  result: Document[];
  is_error: boolean;
  error_message: string;
}

export interface SummarizedResults {
  tool_data: ExecutionResult[];
}

export interface FinalResponse {
  response: string;
  citations: string[];
}

export interface Judgment {
  judgment: string;
  needs_more_info: boolean;
  final_response: FinalResponse;
}

export interface ExecutionPlan {
  plan: string;
  query: string;
  needs_tools: boolean;
  planner_answer: string;
}

export interface ExecutionHistoryEntry {
  query: string;
  plan: ExecutionPlan;
  results: SummarizedResults;
  judgment: Judgment;
}

export interface MemoryContext {
  conversation_history: Message[];
  execution_history: ExecutionHistoryEntry[];
}

// Request/Response Schemas

export interface ProcessQuerySchemaInput {
  memory_context: MemoryContext;
  conversation_id: string;
  title: string;
  openrouter_api_key?: string;
  max_cycles?: number;
}

export interface ProcessQuerySchemaOutput {
  memory_context: MemoryContext;
  conversation_id: string;
  title: string;
  openrouter_api_key?: string;
  max_cycles?: number;
}

export interface AuthResponseSchema {
  status: string;
  authenticated: boolean;
  debug_mode: boolean;
  user?: Record<string, any> | null;
  message?: string | null;
}

export interface LogoutResponseSchema {
  success: boolean;
  message: string;
}

export enum UserRole {
  ADMIN = 'admin',
  USER = 'user'
}

export interface UserSchema {
  email: string;
  name: string;
  role: UserRole;
  created_at?: string | null;
  last_login?: string | null;
}

export interface UserResponseSchema {
  authenticated: boolean;
  user: UserSchema | null;
}

export interface StatusResponseSchema {
  status: 'active' | 'inactive';
  initialized: boolean;
}

export interface ErrorResponseSchema {
  error: string;
}

// Task status types
export enum TaskStatus {
  PENDING = "PENDING",
  PROCESSING = "PROCESSING", 
  COMPLETED = "COMPLETED",
  ERROR = "ERROR"
}

export interface TaskStatusResponse {
  task_id: string; // This is actually the conversation_id for backward compatibility
  status: TaskStatus;
  progress?: number;
  result?: ProcessQuerySchemaOutput;
  error?: string;
  created_at?: string;
  updated_at?: string;
  // DERA cycle tracking
  current_cycle?: number;
  max_cycles?: number;
}

export interface TaskCreationResponse {
  task_id: string;
  status: TaskStatus;
}

// FAQ Types
export interface FAQEntry {
  short_description: string;
  question: string;
}

export interface FAQResponseSchema {
  entries: FAQEntry[];
}

// Mapped API response types to frontend types
export interface ConversationState {
  messages: Message[];
  conversationId: string;
  title: string;
  memoryContext: MemoryContext;
}
